import {
  CsvProduct,
  FirstSection,
  SecondSection,
  SimpleProductCategory,
  SimpleProductResultList
} from "~/models/Product";
import { ID } from "~/models/Identification";
import {
  hasOnlyIndustry,
  Industries,
  Industry,
  IndustryType,
  IndustryTypeValue,
  isIndustry,
  marketPlaceIndustries,
  priceBoardServiceIndustries,
  broadLeafPayIndustries
} from "~/models/Industry";
import { Company } from "~/models/Company";
import { kv } from "~/utils/ExtendFunctions"
import { BasicPackData, OptionData, SelectData } from "~/models/SelectData"
import { ITEM_CATALOG_ID } from '~/data/constants/ItemCatalogId';

function getBasicPack(products: CsvProduct[]) {
  return (products || []).find(product => product.category === "basicPack")
}

type GetInitialBasicPacksProps = {
  simpleProductResult: {
    ssBasic: CsvProduct[]
    reBasic: CsvProduct[]
    coBasic: CsvProduct[]
    edBasic: CsvProduct[]
    mpBasic: CsvProduct[]
    ppBasic: CsvProduct[]
    payBasic: CsvProduct[]
  }
  industries: {
    [key: ID]: Industry
  }
  company: Company
}

export function getInitialBasicPack({
  simpleProductResult: { ssBasic, reBasic, coBasic, edBasic, mpBasic, ppBasic, payBasic },
  industries,
  company
}: GetInitialBasicPacksProps): Partial<BasicPackData> {
  const industryIds = Object.keys(industries)
  const ids: string[] = [
    ...ssBasic,
    ...reBasic,
    ...coBasic,
  ].filter(item => industryIds.includes(`${item.selected}`))
    .map(({ id }) => id)

  if (hasOnlyIndustry(industryIds as IndustryTypeValue[], IndustryType.CarSales)) {
    // 車販しか選択していない場合は、スタートパックを初期選択する
    const basicPack = ssBasic.find(({ id }) => id === ITEM_CATALOG_ID.START_PACK)
    if (basicPack) {
      ids.push(basicPack.id);
    }
  }

  // 電子帳簿保存法改正対応サービスがONの場合
  if (company.electronicDocumentService) {
    // 電帳は現行CSV仕様では判断できないので標準パックを初期選択対象にする
    const basicPack = getBasicPack(edBasic)
    if (basicPack) {
      ids.push(basicPack.id);
    }
  }

  // プライスボード作成支援サービスがONの場合
  if (company.priceBoardService || priceBoardServiceIndustries.some(industry => industries[industry])) {
    const basicPack = getBasicPack(ppBasic)
    if (basicPack) {
      ids.push(basicPack.id);
    }
  }

  // マーケットプレイスがONの場合
  if (company.marketPlace || marketPlaceIndustries.some(industry => industries[industry])) {
    const basicPack = getBasicPack(mpBasic)
    if (basicPack) {
      ids.push(basicPack.id);
    }
  }

  // BLPayがONの場合
  if (company.broadLeafPay || broadLeafPayIndustries.some(industry => industries[industry])) {
    const basicPack = getBasicPack(payBasic)
    if (basicPack) {
      ids.push(basicPack.id);
    }
  }

  return {
    ids
  }
}


type GetInitialOptionsProps = {
  simpleProductResult: {
    common: CsvProduct[]
  }
  company: Company
}

export function getInitialOption({
  simpleProductResult: { common },
  company
}: GetInitialOptionsProps): Partial<OptionData> {
  if (company.count > 1) {
    if (common.some(({ id }) => id === ITEM_CATALOG_ID.ORGANIZATION_MANAGEMENT)) {
      return {
        ids: [ITEM_CATALOG_ID.ORGANIZATION_MANAGEMENT],
        contracts: {
          [ITEM_CATALOG_ID.ORGANIZATION_MANAGEMENT]: company.count - 1
        },
      }
    }
  }
  return {
    ids: [],
    contracts: {},
  }
}

export default class Pack {
  private catalogIds: string[] = []

  constructor(
    private industries: Industries,
    private products: SimpleProductResultList
  ) {
  }

  private _isValid = false

  get isValid() {
    return this._isValid
  }

  static validation({
    industries,
    products
  }: {
    industries: Industries,
    products: SimpleProductResultList
  }) {
    return new this(industries, products)
  }

  validate(selectData: Partial<SelectData>) {
    this.catalogIds = Object.entries(selectData).flatMap(entry => entry[1].ids)

    const exit = (isValid = false) => {
      this._isValid = isValid
      return this
    }

    if (!this.validateBasicPack()) {
      return exit()
    }

    const hasSsBasic = this.hasPack("ssBasic")
    const hasReBasic = this.hasPack("reBasic")
    const hasCoBasic = this.hasPack("coBasic")
    const hasEdBasic = this.hasPack("edBasic")
    const hasPpBasic = this.hasPack("ppBasic")
    const hasMpBasic = this.hasPack("mpBasic")
    const hasPayBasic = this.hasPack("payBasic")

    const products = kv<CsvProduct>(Object.entries(this.products).flatMap(entry => entry[1]))
    const selectProducts = this.catalogIds.map(id => products[id]).filter(product => product)

    const someFirstSection = (firstSections: FirstSection[]) => ({ section1 }: {
      section1: FirstSection
    }) => firstSections.includes(section1)

    const optionSections = [
      FirstSection.Staff,
      FirstSection.Planning,
      FirstSection.Performance,
      FirstSection.DigitalTransformation,
      FirstSection.Marketing,
      FirstSection.Database,
      FirstSection.MaintenanceSupport,
      FirstSection.IntroductionSupport,
      FirstSection.DenchoSupport,
    ]

    if (hasPpBasic) {
      if ([hasSsBasic, hasReBasic, hasCoBasic, hasEdBasic].every(value => !value)) {
        if (selectProducts.some(someFirstSection(optionSections))) {
          return exit()
        }
      }
    }

    if (hasMpBasic) {
      if ([hasSsBasic, hasReBasic, hasCoBasic, hasEdBasic].every(value => !value)) {
        if (selectProducts.some(someFirstSection(optionSections))) {
          return exit()
        }
      }
    }

    if (hasPayBasic) {
      if ([hasSsBasic, hasReBasic, hasCoBasic, hasEdBasic].every(value => !value)) {
        if (selectProducts.some(someFirstSection(optionSections))) {
          return exit()
        }
      }
    }

    return exit(true)
  }

  private hasPack(productCategory: SimpleProductCategory) {
    const productMap = kv<CsvProduct>(this.products[productCategory])
    return this.catalogIds.some(catalogId => productMap[catalogId])
  }

  private validateIndustry() {
    const keysOfIndustry = [
      "ssBasic",
      "reBasic",
      "coBasic",
    ]
    const products = kv<CsvProduct>(keysOfIndustry.flatMap(key => this.products[key]))
    const selectProducts = this.catalogIds.map(id => products[id]).filter(product => product)

    if (this.industries[IndustryType.Maintenance]) {
      if (selectProducts.some(isIndustry([FirstSection.Basic], [SecondSection.Any, SecondSection.Maintenance]))) {
        return true
      }
    }

    if (this.industries[IndustryType.Repair]) {
      if (selectProducts.some(isIndustry([FirstSection.Basic], [SecondSection.Repair]))) {
        return true
      }
    }

    if (this.industries[IndustryType.Service]) {
      if (selectProducts.some(isIndustry([FirstSection.Basic], [SecondSection.Any, SecondSection.Maintenance]))) {
        return true
      }
    }

    if (this.industries[IndustryType.CarSales]) {
      if (selectProducts.some(isIndustry([FirstSection.Basic], [SecondSection.Any]))) {
        return true
      }
    }

    return false
  }

  private validateBasicPack() {
    return this.validateIndustry()
      || this.hasPack("edBasic")
      || this.hasPack("ppBasic")
      || this.hasPack("mpBasic")
      || this.hasPack("payBasic")
  }
}

